import type { ChatMessage } from "@/lib/chat/map-api-message"

export type ChatMessageReactionCounts = Record<string, number>

export type ChatMessageReactionsMap = Record<string, ChatMessageReactionCounts>

export type ViewerReactionsMap = Record<string, string | null>

export function toggleViewerReaction(
  countsMap: ChatMessageReactionsMap,
  viewerMap: ViewerReactionsMap,
  messageId: string,
  emoji: string,
): { counts: ChatMessageReactionsMap; viewer: ViewerReactionsMap } {
  const currentCounts = { ...(countsMap[messageId] ?? {}) }
  const currentViewer = viewerMap[messageId] ?? null

  if (currentViewer === emoji) {
    const nextCount = (currentCounts[emoji] ?? 0) - 1
    if (nextCount <= 0) {
      delete currentCounts[emoji]
    } else {
      currentCounts[emoji] = nextCount
    }
    return {
      counts: { ...countsMap, [messageId]: currentCounts },
      viewer: { ...viewerMap, [messageId]: null },
    }
  }

  if (currentViewer) {
    const prevCount = (currentCounts[currentViewer] ?? 0) - 1
    if (prevCount <= 0) {
      delete currentCounts[currentViewer]
    } else {
      currentCounts[currentViewer] = prevCount
    }
  }

  currentCounts[emoji] = (currentCounts[emoji] ?? 0) + 1

  return {
    counts: { ...countsMap, [messageId]: currentCounts },
    viewer: { ...viewerMap, [messageId]: emoji },
  }
}

export function applyServerReactionAggregate(
  countsMap: ChatMessageReactionsMap,
  viewerMap: ViewerReactionsMap,
  messageId: string,
  counts: Record<string, number>,
  viewerEmoji: string | null,
): { counts: ChatMessageReactionsMap; viewer: ViewerReactionsMap } {
  const normalized: ChatMessageReactionCounts = {}
  for (const [emoji, count] of Object.entries(counts)) {
    if (count > 0) {
      normalized[emoji] = count
    }
  }
  return {
    counts: { ...countsMap, [messageId]: normalized },
    viewer: { ...viewerMap, [messageId]: viewerEmoji },
  }
}

function hasLocalReactionOverride(localViewer: ViewerReactionsMap, messageId: string): boolean {
  return Object.prototype.hasOwnProperty.call(localViewer, messageId)
}

export function mergeReactionMaps(
  serverCounts: ChatMessageReactionsMap,
  localCounts: ChatMessageReactionsMap,
  localViewer: ViewerReactionsMap = {},
): ChatMessageReactionsMap {
  const merged: ChatMessageReactionsMap = { ...serverCounts }
  for (const messageId of Object.keys(localViewer)) {
    if (!hasLocalReactionOverride(localViewer, messageId)) continue
    const local = localCounts[messageId]
    if (local && Object.keys(local).length > 0) {
      merged[messageId] = local
    } else {
      delete merged[messageId]
    }
  }
  return merged
}

export function mergeViewerReactions(
  serverViewer: ViewerReactionsMap,
  localViewer: ViewerReactionsMap,
): ViewerReactionsMap {
  return { ...serverViewer, ...localViewer }
}

export function toReactionCounts(
  raw: Record<string, number> | undefined | null,
): ChatMessageReactionCounts | undefined {
  if (!raw) return undefined
  const counts: ChatMessageReactionCounts = {}
  for (const [emoji, count] of Object.entries(raw)) {
    if (count > 0) {
      counts[emoji] = count
    }
  }
  return Object.keys(counts).length > 0 ? counts : undefined
}

export function buildReactionsStateFromMessages(messages: ChatMessage[]): {
  counts: ChatMessageReactionsMap
  viewer: ViewerReactionsMap
} {
  const counts: ChatMessageReactionsMap = {}
  const viewer: ViewerReactionsMap = {}
  for (const message of messages) {
    if (!message.id) continue
    const parsed = toReactionCounts(message.reactions ?? undefined)
    if (parsed) counts[message.id] = parsed
    if (message.viewerReaction) {
      const trimmed = message.viewerReaction.trim()
      if (trimmed) viewer[message.id] = trimmed
    }
  }
  return { counts, viewer }
}

export function reactionMapsEqual(a: ChatMessageReactionsMap, b: ChatMessageReactionsMap): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const id of aKeys) {
    const left = a[id]
    const right = b[id]
    if (!right) return false
    const leftEmojis = Object.keys(left)
    const rightEmojis = Object.keys(right)
    if (leftEmojis.length !== rightEmojis.length) return false
    for (const emoji of leftEmojis) {
      if (left[emoji] !== right[emoji]) return false
    }
  }
  return true
}

export function viewerMapsEqual(a: ViewerReactionsMap, b: ViewerReactionsMap): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const id of aKeys) {
    if (a[id] !== b[id]) return false
  }
  return true
}

export function mergeReactionsFromMessages(
  prevCounts: ChatMessageReactionsMap,
  prevViewer: ViewerReactionsMap,
  messages: ChatMessage[],
): { counts: ChatMessageReactionsMap; viewer: ViewerReactionsMap; changed: boolean } {
  const server = buildReactionsStateFromMessages(messages)
  const viewer = mergeViewerReactions(server.viewer, prevViewer)
  const counts = mergeReactionMaps(server.counts, prevCounts, viewer)
  const changed = !reactionMapsEqual(counts, prevCounts) || !viewerMapsEqual(viewer, prevViewer)
  return { counts, viewer, changed }
}
